import React, { useMemo, useState } from "react";
import { User } from "src/entities/user.entity";
import { getCookieWithKey } from "src/utils/browserStorage.util";
import { getLangNameByLangCode } from "src/utils/util";
import { showToast } from "src/utils/toast.util";
import { RxCross2 } from "react-icons/rx";
import { useUpsertAICoachLevelVocabulariesMutation } from "../ai-coach-level-vocabulary.service";
import { parseLevelVocabularyYaml, buildVocabularyPreview, AI_COACH_LEVELS } from "../utils/level-vocabulary";
import { UpsertAICoachLevelVocabularyPayload } from "../entities/ai-coach-level-vocabulary.entity";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const ImportLevelVocabulariesYamlModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [upsertLevelVocabularies, { isLoading }] = useUpsertAICoachLevelVocabulariesMutation();
  const [fileName, setFileName] = useState("");
  const [yamlText, setYamlText] = useState("");
  const currentAdmin = useMemo(() => {
    try {
      return getCookieWithKey("current-admin") as User;
    } catch (error) {
      return undefined;
    }
  }, []);

  const parseResult = useMemo(() => {
    if (!yamlText.trim()) {
      return {
        parsedRecords: [] as UpsertAICoachLevelVocabularyPayload[],
        parseError: "",
      };
    }

    try {
      return {
        parsedRecords: parseLevelVocabularyYaml(yamlText),
        parseError: "",
      };
    } catch (error) {
      return {
        parsedRecords: [] as UpsertAICoachLevelVocabularyPayload[],
        parseError: (error as Error).message || "Failed to parse the YAML file.",
      };
    }
  }, [yamlText]);

  const { parsedRecords, parseError } = parseResult;

  if (!isOpen) return null;

  const resetState = () => {
    setFileName("");
    setYamlText("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const content = await file.text();
    setFileName(file.name);
    setYamlText(content);
  };

  const handleImport = async () => {
    if (!parsedRecords.length) {
      showToast("warrning", "Please provide a valid YAML payload first.");
      return;
    }

    try {
      await upsertLevelVocabularies(
        parsedRecords.map((record) => ({
          ...record,
          updatedByAdminId: currentAdmin?.id,
          updatedByAdminName: currentAdmin?.name,
        }))
      ).unwrap();

      showToast("success", `Imported ${parsedRecords.length} language vocabulary configurations successfully.`);
      handleClose();
    } catch (error) {
      showToast(
        "error",
        (error as { error?: string })?.error || "Failed to import AI coach level vocabularies."
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="relative max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 shadow-2xl">
        <div className="flex items-start justify-between gap-6 border-b border-slate-200 bg-white px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">YAML Import</p>
            <h2 className="mt-1 text-2xl font-semibold text-slate-900">Import Level Vocabulary</h2>
            <p className="mt-2 text-sm text-slate-600">
              Upload or paste YAML and push its language vocabulary configuration into{" "}
              <span className="font-semibold text-slate-900">ai_coach_level_vocabularies</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700"
            aria-label="Close modal"
          >
            <RxCross2 className="text-xl" />
          </button>
        </div>

        <div className="max-h-[calc(92vh-92px)] overflow-y-auto px-6 py-6">
          <div className="grid gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
            <section className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Source</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-950">YAML input</h3>
              </div>

              <label className="block rounded-[24px] border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center transition-colors hover:border-slate-400 hover:bg-slate-100">
                <input
                  type="file"
                  accept=".yaml,.yml,text/yaml,text/x-yaml"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <p className="text-sm font-semibold text-slate-900">Choose YAML file</p>
                <p className="mt-2 text-xs text-slate-600">
                  Accepts language blocks such as <span className="font-semibold">nl</span> with `A1` to `C2` string values.
                </p>
              </label>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Selected file</p>
                <p className="mt-2 text-sm text-slate-900">{fileName || "No file selected"}</p>
              </div>

              <div>
                <label htmlFor="levelVocabularyYaml" className="mb-2 block text-sm font-semibold text-slate-800">
                  YAML content
                </label>
                <textarea
                  id="levelVocabularyYaml"
                  value={yamlText}
                  onChange={(event) => setYamlText(event.target.value)}
                  placeholder={'nl:\n  A1: "de, het, een"\n  A2: ""\n  B1: ""\n  B2: ""\n  C1: ""\n  C2: ""'}
                  className="min-h-[360px] w-full rounded-2xl border border-slate-300 bg-white px-4 py-4 font-mono text-[12px] leading-6 text-slate-900 shadow-sm transition-all focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
                />
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={isLoading || !parsedRecords.length}
                  className="rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-950/15 transition-all hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading ? "Importing..." : "Import vocabulary to database"}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-100"
                >
                  Cancel
                </button>
              </div>
            </section>

            <section className="space-y-4 rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Import preview</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-950">Mapped language documents</h3>
              </div>

              <div className="space-y-4 px-5 pb-5">
                {parseError ? (
                  <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800">
                    {parseError}
                  </div>
                ) : null}

                <div className="space-y-3">
                  {parsedRecords.length ? (
                    parsedRecords.map((record) => {
                      const populatedLevels = AI_COACH_LEVELS.filter((level) => record.levels[level].trim()).length;

                      return (
                        <div key={record.langCode} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                              {record.langCode.toUpperCase()}
                            </span>
                            <span className="text-sm font-semibold text-slate-900">
                              {getLangNameByLangCode(record.langCode)}
                            </span>
                            <span className="text-[11px] text-slate-500">
                              {populatedLevels} / {AI_COACH_LEVELS.length} levels populated
                            </span>
                          </div>
                          <p className="mt-3 text-sm leading-6 text-slate-600">
                            {buildVocabularyPreview(record.levels)}
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center">
                      <p className="text-base font-semibold text-slate-900">No import preview yet</p>
                      <p className="mt-2 text-sm text-slate-600">
                        Choose a YAML file or paste YAML text to preview the mapped language vocabulary documents.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
